import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfigProvider, Drawer, Input, Popover, Select, Spin } from 'antd';
import { CheckOutlined, CloseOutlined, DownOutlined, DownloadOutlined, HistoryOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import { useFabricTypes } from '../hooks/useFabricTypes.jsx';
import { useStockLocations } from '../hooks/useStockLocations.jsx';

// Design tokens ported from the "Ready Stock Redesign" mock — the same shell
// palette DefaultLayout publishes, re-declared here so a nested ConfigProvider's
// portalled popovers/drawers can read the same hex values.
import { PRIMARY, hexToRgba, SHELL_THEME as THEME, shellAntdTheme, STATUS } from '../shellTokens';

const PAGE_SIZE_OPTIONS = [15, 30, 50];
const REASON_PRESETS = ['Factory batch', 'Stock-take', 'Damaged'];

/**
 * Swatch hues for the seeded fabric colours (FabricColorSeeder). The colour
 * table stores names only — no hex column — so the dot is a rendering of the
 * name, not extra data. Anything unrecognised gets a neutral chip.
 */
const COLOR_HEX = {
  'maroon': '#6f2f3e', 'deep maroon': '#571f2c', 'violet': '#7c5bb5', 'coffee': '#5a4030',
  'milk coffee': '#8a6b52', 'ash': '#8d949e', 'brown': '#6b4a31', 'deep brown': '#4a3120',
  'green': '#3d6b52', 'sea green': '#2e6b62', 'golden': '#8a7433', 'navy blue': '#243a63',
  'royal blue': '#2b4a91', 'sky blue': '#4a86b8', 'cream': '#d8cdb4', 'lemon': '#c9c356',
  'olive': '#6b6b3a', 'gray': '#6c727c', 'orange': '#b5713a', 'pink': '#b56b85',
  'purple': '#6b4a8a', 'silver': '#a8adb6', 'red': '#9e3a3a', 'white': '#e8e2d5',
  'black': '#2a2d33', 'paste': '#5fa3a3',
};

/** Ledger vocabulary — label, tint and sign, in one place. */
const MOVEMENT_META = {
  RETURN_IN: { label: 'RETURN IN', tint: STATUS.ok },
  CANCEL_IN: { label: 'CANCEL IN', tint: STATUS.ok },
  MANUAL_IN: { label: 'MANUAL IN', tint: 'var(--activeText)', bg: 'var(--activeBg)' },
  ADJUSTMENT: { label: 'ADJUSTMENT', tint: STATUS.warn },
  PULL_OUT: { label: 'PULL OUT', tint: STATUS.errorSoft },
};

const movementMeta = (type) => MOVEMENT_META[type] || { label: type || '—', tint: 'var(--muted)' };

const initials = (name) => (name || '')
  .split(/\s+/).filter(Boolean).slice(0, 2)
  .map((w) => w.charAt(0).toUpperCase()).join('') || '?';

const fmtAgo = (days) => {
  if (days === null || days === undefined) return null;
  return days === 0 ? 'today' : `${days}d`;
};

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

/** window width < 768, matching the mock's card-list breakpoint (1c). */
const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

const GRID = 'minmax(0,2.2fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1.15fr) minmax(0,1.1fr) 110px 210px';

function ReadyStock() {
  const axiosClient = useAxiosClient();
  const { permissions, darkMode } = useStateContext();
  const isMobile = useIsMobile();

  const canManage = (permissions || []).includes('MANAGE_STOCK');

  const { fabricTypes } = useFabricTypes();
  const { stockLocations, fetchStockLocations } = useStockLocations();

  const [kinds, setKinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [matchedUnits, setMatchedUnits] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortField, setSortField] = useState('onHand');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stats, setStats] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [fabricTypeId, setFabricTypeId] = useState(null);
  const [locationId, setLocationId] = useState(null); // id | 'none' | null
  const [inStockOnly, setInStockOnly] = useState(true);
  const [sittingOnly, setSittingOnly] = useState(false);
  const [openFilter, setOpenFilter] = useState(null); // 'fabric' | 'location' | 'rows' | null

  // Per-kind history drawer
  const [historyKind, setHistoryKind] = useState(null);
  const [movements, setMovements] = useState([]);
  const [historySummary, setHistorySummary] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Company-wide ledger drawer
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Add / adjust sheet
  const [entryKind, setEntryKind] = useState(null);
  const [entryMode, setEntryMode] = useState('add'); // 'add' | 'adjust'
  const [entryQty, setEntryQty] = useState(1);
  const [entryReason, setEntryReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Placement sheet
  const [placementKind, setPlacementKind] = useState(null);
  const [placementLocation, setPlacementLocation] = useState(null);
  const [placementBin, setPlacementBin] = useState('');

  const searchBoxRef = useRef(null);

  const fetchStock = useCallback(async (overrides = {}) => {
    const p = overrides.page ?? page;
    const search = 'search' in overrides ? overrides.search : searchText;
    const fabric = 'fabricTypeId' in overrides ? overrides.fabricTypeId : fabricTypeId;
    const location = 'locationId' in overrides ? overrides.locationId : locationId;
    const stocked = 'inStockOnly' in overrides ? overrides.inStockOnly : inStockOnly;
    const sitting = 'sittingOnly' in overrides ? overrides.sittingOnly : sittingOnly;
    const size = overrides.pageSize ?? pageSize;
    const field = overrides.sortField ?? sortField;
    const order = overrides.sortOrder ?? sortOrder;

    setLoading(true);
    try {
      const res = await axiosClient.get('/stock/index', {
        params: {
          page: p,
          perPage: size,
          sortField: field,
          sortOrder: order,
          inStockOnly: stocked,
          ...(sitting ? { sittingOnly: true } : {}),
          ...(search ? { search } : {}),
          ...(fabric ? { fabricTypeId: fabric } : {}),
          ...(location ? { locationId: location } : {}),
        },
      });
      setKinds(res.data.data || []);
      setTotal(res.data.meta?.total ?? 0);
      setMatchedUnits(res.data.summary?.units ?? 0);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [axiosClient, page, searchText, fabricTypeId, locationId, inStockOnly, sittingOnly, pageSize, sortField, sortOrder]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axiosClient.get('/stock/stats');
      setStats(res.data);
    } catch {
      // Tiles are supplementary — the list still works without them.
    }
  }, [axiosClient]);

  useEffect(() => { fetchStock(); }, [fetchStock]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // "/" focuses search, matching the affordance the mock draws on the search box.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        searchBoxRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const applyFilter = (patch) => {
    if ('search' in patch) setSearchText(patch.search);
    if ('fabricTypeId' in patch) setFabricTypeId(patch.fabricTypeId);
    if ('locationId' in patch) setLocationId(patch.locationId);
    if ('inStockOnly' in patch) setInStockOnly(patch.inStockOnly);
    if ('sittingOnly' in patch) setSittingOnly(patch.sittingOnly);
    if ('pageSize' in patch) setPageSize(patch.pageSize);
    setOpenFilter(null);
    setPage(1);
    fetchStock({ ...patch, page: 1 });
  };

  const clearAllFilters = () => applyFilter({ search: '', fabricTypeId: null, locationId: null, sittingOnly: false });

  const toggleSort = (field) => {
    const nextOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortOrder(nextOrder);
    fetchStock({ sortField: field, sortOrder: nextOrder });
  };

  const goToPage = (p) => {
    setPage(p);
    fetchStock({ page: p });
  };

  const refreshAfterMutation = () => {
    fetchStock();
    fetchStats();
  };

  // -- History --
  const openHistory = async (kind) => {
    setHistoryKind(kind);
    setMovements([]);
    setHistorySummary(null);
    setHistoryLoading(true);
    try {
      const res = await axiosClient.get(`/stock/movements/${kind.id}`);
      setMovements(res.data.data || []);
      setHistorySummary(res.data.summary || null);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openLedger = async () => {
    setLedgerOpen(true);
    setLedger([]);
    setLedgerLoading(true);
    try {
      const res = await axiosClient.get('/stock/movements', { params: { perPage: 50 } });
      setLedger(res.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLedgerLoading(false);
    }
  };

  // -- Add / adjust --
  const openEntry = (kind, mode) => {
    setEntryKind(kind);
    setEntryMode(mode);
    setEntryQty(mode === 'adjust' ? -1 : 1);
    setEntryReason('');
  };

  const submitEntry = async () => {
    if (!entryQty || Number.isNaN(entryQty)) {
      toast.error('Enter a quantity');
      return;
    }
    setSaving(true);
    try {
      await axiosClient.post('/stock/movements', {
        productKindId: entryKind.id,
        quantity: entryMode === 'adjust' ? entryQty : Math.abs(entryQty),
        type: entryMode === 'adjust' ? 'ADJUSTMENT' : 'MANUAL_IN',
        reason: entryReason || null,
      });
      toast.success('Stock updated');
      setEntryKind(null);
      refreshAfterMutation();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  // -- Placement --
  const openPlacement = (kind) => {
    setPlacementKind(kind);
    setPlacementLocation(kind.locationId ?? null);
    setPlacementBin(kind.bin || '');
  };

  const submitPlacement = async () => {
    setSaving(true);
    try {
      await axiosClient.put(`/stock/placement/${placementKind.id}`, {
        stockLocationId: placementLocation,
        bin: placementBin,
      });
      toast.success('Placement updated');
      setPlacementKind(null);
      fetchStock();
      fetchStockLocations();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  // Current page only — an honest label for what's actually exported, since
  // there's no backend endpoint for a full-catalogue export.
  const handleExport = () => {
    const header = ['Product type', 'Formation', 'Fabric', 'Color', 'Location', 'Bin', 'Last movement', 'Days ago', 'On hand'];
    const rows = kinds.map((k) => [
      k.productType, k.productFormation, k.fabricType, k.fabricColor,
      k.location || '', k.bin || '',
      k.lastMovementType || '', k.daysSinceMovement ?? '', k.onHand,
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ready-stock-page-${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? 'dark' : 'light']).map(([k, v]) => [`--${k}`, v])
    ),
    '--primary': PRIMARY,
  };

  // The Drawer panel itself portals to <body>, outside the element rootStyle
  // declares the variables on, so it needs the plain hex.
  const panelBg = THEME[darkMode ? 'dark' : 'light'].surface;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  const hasActiveFilters = fabricTypeId !== null || locationId !== null || sittingOnly;
  const showEmpty = !loading && kinds.length === 0;

  const locationName = useMemo(
    () => (locationId === 'none' ? 'Not placed' : stockLocations.find((l) => l.id === locationId)?.name),
    [locationId, stockLocations],
  );
  const fabricName = useMemo(
    () => fabricTypes.find((f) => f.id === fabricTypeId)?.name,
    [fabricTypeId, fabricTypes],
  );

  const entryNext = entryKind
    ? entryKind.onHand + (entryMode === 'adjust' ? (entryQty || 0) : Math.abs(entryQty || 0))
    : 0;
  const entryNegative = entryMode === 'adjust' && (entryQty || 0) < 0;

  return (
    <div className="et-stock" style={rootStyle}>
      <style>{`
        .et-stock{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-stock__row:hover{background:var(--hover)}
        .et-stock__card:hover{border-color:var(--borderStrong)}
        .et-stock__ghost:hover{background:var(--hover)}
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em' }}>Ready Stock</div>
          <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            Finished units on hand, per product kind. Company-wide across teams.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="et-stock__ghost" title="Export this page as CSV" onClick={handleExport} style={ghostBtn}>
            <DownloadOutlined style={{ color: 'var(--muted)', fontSize: 12 }} /> Export
          </button>
          <button type="button" className="et-stock__ghost" onClick={openLedger} style={ghostBtn}>
            <HistoryOutlined style={{ color: 'var(--muted)', fontSize: 12 }} /> All movements
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => (kinds.length ? openEntry(kinds[0], 'add') : toast.info('No product kind in view to add stock to'))}
              style={primaryBtn}
            >
              + Add stock
            </button>
          )}
        </div>
      </div>

      {/* KPI tiles */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 14 }}>
          <StatTile
            icon="▦" tint={PRIMARY} label="Units on hand"
            value={stats.unitsOnHand} deltaPct={stats.unitsOnHandChangePct} deltaGoodWhenUp
            foot={`across ${stats.kindsWithStock} product kind${stats.kindsWithStock === 1 ? '' : 's'}`}
          />
          <StatTile
            icon="↩" tint={STATUS.ok} label={`Returned in · ${stats.windowDays}d`}
            value={stats.returnedIn} deltaPct={stats.returnedInChangePct}
            foot={`from ${stats.returnedInOrders} order${stats.returnedInOrders === 1 ? '' : 's'}`}
          />
          <StatTile
            icon="⇄" tint="#a78bfa" label={`Pulled to orders · ${stats.windowDays}d`}
            value={stats.pulledOut} deltaPct={stats.pulledOutChangePct} deltaGoodWhenUp
            foot="sold straight from stock"
          />
          <StatTile
            icon="◷" tint={STATUS.warn} label={`Sitting ${stats.sittingDays}d+`}
            value={stats.sittingUnits}
            note={stats.sittingPctOfStock !== null ? `${stats.sittingPctOfStock}% of stock` : null}
            foot={stats.sittingKinds
              ? `${stats.sittingKinds} kind${stats.sittingKinds === 1 ? '' : 's'}${stats.sittingSince ? ` untouched since ${dayjs(stats.sittingSince).format('D MMM')}` : ' never moved'}`
              : 'nothing sitting idle'}
            active={sittingOnly}
            onClick={() => applyFilter({ sittingOnly: !sittingOnly, inStockOnly: true })}
          />
        </div>
      )}

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

          {/* Toolbar */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div style={{ font: '700 14.5px Outfit, sans-serif' }}>Product kinds</div>
              <div style={{ font: '600 11.5px monospace', color: 'var(--dim)', padding: '2px 7px', borderRadius: 9, background: 'var(--surface2)' }}>{total}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Input
                ref={searchBoxRef}
                allowClear
                placeholder="Search product, fabric, color, formation or location"
                prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
                suffix={<span style={{ font: '600 10.5px monospace', color: 'var(--dim)', padding: '2px 6px', borderRadius: 5, background: 'var(--surface)', border: '1px solid var(--border)' }}>/</span>}
                style={{ width: isMobile ? '100%' : 320 }}
                value={searchInput}
                // Emptying the box re-runs the query — antd 5.4's allowClear
                // has no onClear callback, it just fires onChange with ''.
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  if (e.target.value === '' && searchText !== '') applyFilter({ search: '' });
                }}
                onPressEnter={() => applyFilter({ search: searchInput })}
              />
              <Popover
                trigger="click"
                open={openFilter === 'fabric'}
                onOpenChange={(o) => setOpenFilter(o ? 'fabric' : null)}
                content={
                  <MenuList
                    items={[{ key: null, label: 'All fabrics' }, ...fabricTypes.map((f) => ({ key: f.id, label: f.name }))]}
                    active={fabricTypeId}
                    onSelect={(v) => applyFilter({ fabricTypeId: v })}
                    darkMode={darkMode}
                  />
                }
              >
                <button type="button" style={filterBtn}>
                  {fabricName || 'Fabric'} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                </button>
              </Popover>
              <Popover
                trigger="click"
                open={openFilter === 'location'}
                onOpenChange={(o) => setOpenFilter(o ? 'location' : null)}
                content={
                  <MenuList
                    items={[
                      { key: null, label: 'All locations' },
                      ...stockLocations.map((l) => ({ key: l.id, label: l.name })),
                      { key: 'none', label: 'Not placed' },
                    ]}
                    active={locationId}
                    onSelect={(v) => applyFilter({ locationId: v })}
                    hint={stockLocations.length === 0 ? 'No stock locations yet — add them in Product Settings.' : undefined}
                    darkMode={darkMode}
                  />
                }
              >
                <button type="button" style={filterBtn}>
                  {locationName || 'Location'} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                </button>
              </Popover>
              <div
                role="button" tabIndex={0}
                onClick={() => applyFilter({ inStockOnly: !inStockOnly })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilter({ inStockOnly: !inStockOnly }); }}
                style={{
                  height: 32, padding: '0 11px', borderRadius: 8,
                  border: `1px solid ${inStockOnly ? hexToRgba(PRIMARY, .35) : 'var(--border)'}`,
                  background: inStockOnly ? 'var(--activeBg)' : 'var(--surface2)',
                  display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                }}
              >
                <span style={{ width: 26, height: 15, borderRadius: 8, background: inStockOnly ? PRIMARY : 'var(--borderStrong)', display: 'flex', alignItems: 'center', justifyContent: inStockOnly ? 'flex-end' : 'flex-start', padding: '0 2px' }}>
                  <span style={{ width: 11, height: 11, borderRadius: 6, background: '#fff' }} />
                </span>
                <span style={{ font: '600 12px Outfit, sans-serif', color: inStockOnly ? 'var(--activeText)' : 'var(--muted)' }}>In stock only</span>
              </div>
            </div>
          </div>

          {/* Filter chips */}
          {hasActiveFilters && (
            <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--dim)', letterSpacing: '.05em', textTransform: 'uppercase', marginRight: 2 }}>Filters</span>
              {fabricTypeId !== null && <Chip label="Fabric" value={fabricName} onClear={() => applyFilter({ fabricTypeId: null })} />}
              {locationId !== null && <Chip label="Location" value={locationName} onClear={() => applyFilter({ locationId: null })} />}
              {sittingOnly && <Chip label="Sitting" value={`${stats?.sittingDays ?? 60}d+`} tone="warn" onClear={() => applyFilter({ sittingOnly: false })} />}
              <button type="button" onClick={clearAllFilters} style={{ marginLeft: 'auto', height: 26, padding: '0 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', font: '600 12px Outfit, sans-serif', cursor: 'pointer' }}>
                Clear all
              </button>
            </div>
          )}

          {/* Desktop table */}
          {!isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div style={{ minWidth: 940 }}>
                <div style={{ display: 'grid', gridTemplateColumns: GRID, background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  {[
                    { key: 'kind', label: 'Product kind', pad: '9px 16px' },
                    { key: 'fabric', label: 'Fabric' },
                    { key: 'color', label: 'Color' },
                    { key: 'location', label: 'Location' },
                    { key: 'lastMovement', label: 'Last movement', sortable: true },
                    { key: 'onHand', label: 'On hand', sortable: true, align: 'flex-end' },
                    { key: 'action', label: 'Action', align: 'flex-end', pad: '9px 16px 9px 8px' },
                  ].map((h) => (
                    <div
                      key={h.key}
                      onClick={h.sortable ? () => toggleSort(h.key) : undefined}
                      style={{
                        padding: h.pad || '9px 8px', display: 'flex', alignItems: 'center', gap: 5,
                        justifyContent: h.align || 'flex-start',
                        font: '700 11.5px Outfit, sans-serif',
                        color: sortField === h.key ? 'var(--activeText)' : 'var(--muted)',
                        letterSpacing: '.05em', textTransform: 'uppercase',
                        cursor: h.sortable ? 'pointer' : 'default',
                      }}
                    >
                      {h.label}
                      {h.sortable && sortField === h.key && <span style={{ color: PRIMARY, fontSize: 9 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                    </div>
                  ))}
                </div>

                {loading && <LoadingRow />}

                {showEmpty && (
                  <EmptyState
                    search={searchText} inStockOnly={inStockOnly} hasFilters={hasActiveFilters}
                    onShowAll={() => applyFilter({ inStockOnly: false })}
                    onClear={() => { setSearchInput(''); clearAllFilters(); }}
                  />
                )}

                {!loading && kinds.map((kind) => {
                  const sitting = kind.onHand > 0 && (kind.daysSinceMovement ?? 999) >= (stats?.sittingDays ?? 60);
                  const meta = movementMeta(kind.lastMovementType);
                  return (
                    <div
                      key={kind.id}
                      className="et-stock__row"
                      style={{
                        display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid var(--border)',
                        background: sitting ? 'rgba(251,191,36,.04)' : 'transparent',
                        opacity: kind.onHand > 0 ? 1 : 0.72,
                      }}
                    >
                      <div style={{ padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                        <span style={{ flex: 'none', width: 32, height: 32, borderRadius: 9, background: kind.onHand > 0 ? hexToRgba(PRIMARY, .16) : 'var(--surface2)', color: kind.onHand > 0 ? 'var(--activeText)' : 'var(--muted)', font: '600 11px monospace', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {initials(kind.productType)}
                        </span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', font: '600 13px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {kind.productType}
                            {sitting && <span style={{ font: '600 10.5px Outfit, sans-serif', color: STATUS.warn, background: 'rgba(251,191,36,.14)', padding: '2px 7px', borderRadius: 8, marginLeft: 6 }}>Sitting {kind.daysSinceMovement ?? '—'}d</span>}
                          </span>
                          <span style={{ display: 'block', font: '400 11.5px monospace', color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {kind.productFormation}
                          </span>
                        </span>
                      </div>
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ font: '600 11px Outfit, sans-serif', color: '#a78bfa', background: 'rgba(139,92,246,.14)', padding: '3px 8px', borderRadius: 5 }}>{kind.fabricType}</span>
                      </div>
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                        <ColorSwatch name={kind.fabricColor} />
                        <span style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kind.fabricColor}</span>
                      </div>
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                        <Placement kind={kind} />
                      </div>
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                        {kind.lastMovementType ? (
                          <>
                            <span style={{ font: '600 10.5px monospace', color: meta.tint, background: meta.bg || `${meta.tint}24`, padding: '2px 6px', borderRadius: 5, whiteSpace: 'nowrap' }}>{meta.label}</span>
                            <span style={{ font: '400 12px monospace', color: sitting ? STATUS.warn : 'var(--muted)' }}>{fmtAgo(kind.daysSinceMovement)}</span>
                          </>
                        ) : (
                          <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>Never moved</span>
                        )}
                      </div>
                      <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', font: '700 15px monospace', color: kind.onHand > 0 ? STATUS.ok : 'var(--dim)' }}>
                        {kind.onHand}
                      </div>
                      <div style={{ padding: '7px 16px 7px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 7 }}>
                        <button type="button" className="et-stock__ghost" onClick={() => openHistory(kind)} style={rowBtn}>History</button>
                        {canManage && <button type="button" onClick={() => openEntry(kind, 'add')} style={rowBtnPrimary}>+ Add</button>}
                        {canManage && (
                          <button type="button" className="et-stock__ghost" title="Set location" onClick={() => openPlacement(kind)} style={{ ...rowBtn, width: 30, padding: 0, justifyContent: 'center', color: 'var(--muted)' }}>⇅</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mobile card list */}
          {isMobile && (
            <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 9 }}>
              {loading && <LoadingRow />}
              {showEmpty && (
                <EmptyState
                  search={searchText} inStockOnly={inStockOnly} hasFilters={hasActiveFilters}
                  onShowAll={() => applyFilter({ inStockOnly: false })}
                  onClear={() => { setSearchInput(''); clearAllFilters(); }}
                />
              )}
              {!loading && kinds.map((kind) => {
                const sitting = kind.onHand > 0 && (kind.daysSinceMovement ?? 999) >= (stats?.sittingDays ?? 60);
                const meta = movementMeta(kind.lastMovementType);
                return (
                  <div
                    key={kind.id}
                    className="et-stock__card"
                    style={{
                      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
                      padding: '12px 13px', display: 'flex', flexDirection: 'column', gap: 10,
                      boxShadow: sitting ? `inset 3px 0 0 ${STATUS.warn}` : 'none',
                      opacity: kind.onHand > 0 ? 1 : 0.72,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ font: '600 14px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {kind.productType} · {kind.productFormation}
                        </div>
                        <div style={{ font: '400 11.5px monospace', color: 'var(--dim)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {kind.fabricType} · {kind.fabricColor} · <span style={{ color: 'var(--muted)' }}>{kind.location ? `${kind.location}${kind.bin ? ` · ${kind.bin}` : ''}` : 'Not placed'}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ font: '700 20px monospace', color: kind.onHand > 0 ? STATUS.ok : 'var(--dim)', lineHeight: 1 }}>{kind.onHand}</div>
                        <div style={{ font: '400 10.5px Outfit, sans-serif', color: 'var(--dim)', marginTop: 3 }}>on hand</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {sitting ? (
                        <span style={{ font: '600 10.5px Outfit, sans-serif', color: STATUS.warn, background: 'rgba(251,191,36,.14)', padding: '3px 8px', borderRadius: 8 }}>Sitting {kind.daysSinceMovement}d</span>
                      ) : kind.lastMovementType ? (
                        <span style={{ font: '600 10.5px monospace', color: meta.tint, background: meta.bg || `${meta.tint}24`, padding: '3px 7px', borderRadius: 5 }}>{meta.label}</span>
                      ) : (
                        <span style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>Never moved</span>
                      )}
                      {kind.daysSinceMovement !== null && kind.daysSinceMovement !== undefined && (
                        <span style={{ marginLeft: 'auto', font: '400 11.5px monospace', color: 'var(--dim)' }}>{fmtAgo(kind.daysSinceMovement)} ago</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                      <button type="button" onClick={() => openHistory(kind)} style={mobileActionBtn}>History</button>
                      {canManage && (
                        <button type="button" onClick={() => openEntry(kind, 'add')} style={{ ...mobileActionBtn, border: `1px solid ${hexToRgba(PRIMARY, .4)}`, background: 'var(--activeBg)', color: 'var(--activeText)' }}>+ Add</button>
                      )}
                      {canManage && (
                        <button type="button" title="Set location" onClick={() => openPlacement(kind)} style={{ ...mobileActionBtn, flex: 'none', width: 44, color: 'var(--muted)' }}>⇅</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
                {total === 0 ? '0 kinds' : (
                  <>
                    Showing <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{rangeStart}–{rangeEnd}</span>{' '}
                    of <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{total}</span> kinds ·{' '}
                    <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{matchedUnits}</span> units
                  </>
                )}
              </div>
              <Popover
                trigger="click"
                open={openFilter === 'rows'}
                onOpenChange={(o) => setOpenFilter(o ? 'rows' : null)}
                content={<MenuList items={PAGE_SIZE_OPTIONS.map((n) => ({ key: n, label: String(n) }))} active={pageSize} onSelect={(v) => applyFilter({ pageSize: v })} darkMode={darkMode} />}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
                  <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>Rows</span>
                  <span style={{ height: 28, padding: '0 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', font: '600 12px monospace', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {pageSize}<DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                  </span>
                </span>
              </Popover>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <PageBtn disabled={page <= 1} onClick={() => goToPage(page - 1)}>‹</PageBtn>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .map((p, i, arr) => (
                  <React.Fragment key={p}>
                    {i > 0 && arr[i - 1] !== p - 1 && <span style={{ color: 'var(--dim)', fontSize: 12 }}>…</span>}
                    <PageBtn active={p === page} onClick={() => goToPage(p)}>{p}</PageBtn>
                  </React.Fragment>
                ))}
              <PageBtn disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>›</PageBtn>
            </div>
          </div>
        </div>

        {/* History drawer — one Product Kind's ledger */}
        <Drawer
          open={!!historyKind}
          onClose={() => setHistoryKind(null)}
          width={isMobile ? '100%' : 520}
          closable={false}
          bodyStyle={{ padding: 0, background: panelBg }}
          drawerStyle={{ background: panelBg }}
        >
          <div style={rootStyle}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ font: '700 14.5px Outfit, sans-serif', color: 'var(--text)' }}>History</div>
                <div style={{ font: '400 11.5px monospace', color: 'var(--dim)', marginTop: 3 }}>
                  {historyKind && [historyKind.productType, historyKind.fabricType, historyKind.fabricColor, historyKind.productFormation].filter(Boolean).join(' / ')}
                </div>
              </div>
              <span onClick={() => setHistoryKind(null)} style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--surface2)', color: 'var(--muted)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <CloseOutlined />
              </span>
            </div>

            {historySummary && (
              <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', alignItems: 'center', gap: 16 }}>
                <SummaryStat label="On hand" value={historySummary.onHand} tint={STATUS.ok} />
                <span style={{ width: 1, height: 28, background: 'var(--border)' }} />
                <SummaryStat label={`In · ${historySummary.windowDays}d`} value={`+${historySummary.in}`} tint="var(--text)" />
                <span style={{ width: 1, height: 28, background: 'var(--border)' }} />
                <SummaryStat label={`Out · ${historySummary.windowDays}d`} value={historySummary.out} tint={STATUS.errorSoft} />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) 70px minmax(0,1.2fr) 90px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
              {['Type', 'Qty', 'By', 'When'].map((h, i) => (
                <div key={h} style={{ padding: i === 0 ? '10px 16px' : i === 3 ? '10px 16px 10px 8px' : '10px 8px', textAlign: i === 1 ? 'right' : 'left', font: '700 11px Outfit, sans-serif', color: 'var(--muted)', letterSpacing: '.05em', textTransform: 'uppercase' }}>{h}</div>
              ))}
            </div>

            {historyLoading && <LoadingRow />}
            {!historyLoading && movements.length === 0 && (
              <div style={{ padding: '32px 18px', textAlign: 'center', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                No movements recorded for this kind yet.
              </div>
            )}
            {movements.map((m) => <MovementRow key={m.id} movement={m} />)}
          </div>
        </Drawer>

        {/* All-movements drawer — the company-wide ledger */}
        <Drawer
          open={ledgerOpen}
          onClose={() => setLedgerOpen(false)}
          width={isMobile ? '100%' : 560}
          closable={false}
          bodyStyle={{ padding: 0, background: panelBg }}
          drawerStyle={{ background: panelBg }}
        >
          <div style={rootStyle}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ font: '700 14.5px Outfit, sans-serif', color: 'var(--text)' }}>All movements</div>
                <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', marginTop: 3 }}>The 50 most recent entries, company-wide.</div>
              </div>
              <span onClick={() => setLedgerOpen(false)} style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--surface2)', color: 'var(--muted)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <CloseOutlined />
              </span>
            </div>
            {ledgerLoading && <LoadingRow />}
            {!ledgerLoading && ledger.length === 0 && (
              <div style={{ padding: '32px 18px', textAlign: 'center', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                Nothing has moved through ready stock yet.
              </div>
            )}
            {ledger.map((m) => <MovementRow key={m.id} movement={m} showKind />)}
          </div>
        </Drawer>

        {/* Add / adjust sheet */}
        <Sheet
          open={!!entryKind}
          onClose={() => setEntryKind(null)}
          rootStyle={rootStyle}
          title="Add / adjust stock"
          header={
            <div style={{ display: 'flex', padding: 3, borderRadius: 9, background: 'var(--surface2)', border: '1px solid var(--border)', gap: 2 }}>
              {[['add', 'Add'], ['adjust', 'Adjust']].map(([mode, label]) => (
                <span
                  key={mode}
                  onClick={() => { setEntryMode(mode); setEntryQty(mode === 'adjust' ? -1 : 1); }}
                  style={{
                    height: 26, padding: '0 12px', borderRadius: 7,
                    background: entryMode === mode ? PRIMARY : 'transparent',
                    color: entryMode === mode ? '#fff' : 'var(--muted)',
                    font: '600 12px Outfit, sans-serif', display: 'flex', alignItems: 'center', cursor: 'pointer',
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          }
        >
          {entryKind && (
            <>
              {/* Which kind, and where it lives — the same identity line the mock shows */}
              <div style={{ padding: '11px 13px', borderRadius: 11, background: 'var(--surface2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ font: '600 13px Outfit, sans-serif' }}>{entryKind.productType} · {entryKind.productFormation}</div>
                  <div style={{ font: '400 11.5px monospace', color: 'var(--dim)', marginTop: 2 }}>
                    {entryKind.fabricType} · {entryKind.fabricColor} · <span style={{ color: 'var(--muted)' }}>{entryKind.location ? `${entryKind.location}${entryKind.bin ? ` · ${entryKind.bin}` : ''}` : 'Not placed'}</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flex: 'none' }}>
                  <div style={{ font: '600 10.5px Outfit, sans-serif', color: 'var(--dim)', letterSpacing: '.05em', textTransform: 'uppercase' }}>On hand</div>
                  <div style={{ font: '700 16px monospace', color: STATUS.ok }}>{entryKind.onHand}</div>
                </div>
              </div>

              {/* Choose a different kind without leaving the sheet — the header
                  "+ Add stock" button opens here on whatever row is first. */}
              <Field label="Product kind">
                <Select
                  value={entryKind.id}
                  onChange={(id) => setEntryKind(kinds.find((k) => k.id === id) || entryKind)}
                  style={{ width: '100%' }}
                  // The sheet is a plain overlay at z-index 1100; the dropdown
                  // portals to <body> at antd's default 1050, so it needs raising.
                  dropdownStyle={{ zIndex: 1200 }}
                  showSearch
                  optionFilterProp="label"
                  options={kinds.map((k) => ({
                    value: k.id,
                    label: `${k.productType} · ${k.productFormation} · ${k.fabricType} · ${k.fabricColor}`,
                  }))}
                />
              </Field>

              <Field label={entryMode === 'adjust' ? 'Adjustment' : 'Quantity to add'}>
                <div style={{
                  height: 40, borderRadius: 9,
                  border: `1px solid ${entryNegative ? 'rgba(248,113,113,.4)' : hexToRgba(PRIMARY, .45)}`,
                  background: entryNegative ? 'rgba(248,113,113,.06)' : 'var(--surface2)',
                  display: 'flex', alignItems: 'center', padding: '0 6px 0 13px', gap: 10,
                  boxShadow: entryNegative ? 'none' : `0 0 0 3px ${hexToRgba(PRIMARY, .12)}`,
                }}>
                  <input
                    type="number"
                    value={entryQty}
                    onChange={(e) => setEntryQty(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', font: '700 14px monospace', color: entryNegative ? STATUS.errorSoft : 'var(--text)' }}
                  />
                  <span onClick={() => setEntryQty((q) => (Number(q) || 0) - 1)} style={stepBtn}>−</span>
                  <span onClick={() => setEntryQty((q) => (Number(q) || 0) + 1)} style={{ ...stepBtn, color: 'var(--text)' }}>+</span>
                </div>
                {entryMode === 'adjust' && (
                  <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--muted)', marginTop: 5 }}>
                    A negative number removes units; stock can't go below zero.
                  </div>
                )}
              </Field>

              <Field label={<>Reason <span style={{ color: 'var(--dim)', fontWeight: 400 }}>optional</span></>}>
                <Input
                  placeholder="e.g. factory batch, stock-take correction"
                  value={entryReason}
                  onChange={(e) => setEntryReason(e.target.value)}
                />
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 7 }}>
                  {REASON_PRESETS.map((r) => (
                    <span key={r} onClick={() => setEntryReason(r)} style={{
                      font: '600 11.5px Outfit, sans-serif',
                      color: entryReason === r ? 'var(--activeText)' : 'var(--muted)',
                      background: entryReason === r ? 'var(--activeBg)' : 'var(--surface2)',
                      border: `1px solid ${entryReason === r ? hexToRgba(PRIMARY, .35) : 'var(--border)'}`,
                      padding: '4px 9px', borderRadius: 7, cursor: 'pointer',
                    }}>{r}</span>
                  ))}
                </div>
              </Field>

              <div style={{
                padding: '11px 13px', borderRadius: 11,
                background: entryNegative ? 'rgba(248,113,113,.06)' : 'rgba(52,211,153,.06)',
                border: `1px solid ${entryNegative ? 'rgba(248,113,113,.24)' : 'rgba(52,211,153,.24)'}`,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>After this entry</span>
                <span style={{ marginLeft: 'auto', font: '600 12.5px monospace', color: 'var(--muted)' }}>{entryKind.onHand}</span>
                <span style={{ color: 'var(--dim)', fontSize: 11 }}>→</span>
                <span style={{ font: '700 15px monospace', color: entryNegative ? STATUS.errorSoft : STATUS.ok }}>{entryNext}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9 }}>
                <button type="button" onClick={() => setEntryKind(null)} style={sheetCancelBtn}>Cancel</button>
                <button type="button" disabled={saving || entryNext < 0} onClick={submitEntry} style={{ ...sheetOkBtn, opacity: saving || entryNext < 0 ? 0.6 : 1 }}>
                  {entryMode === 'adjust' ? 'Apply adjustment' : 'Add to stock'}
                </button>
              </div>
            </>
          )}
        </Sheet>

        {/* Placement sheet — where the kind is kept */}
        <Sheet
          open={!!placementKind}
          onClose={() => setPlacementKind(null)}
          rootStyle={rootStyle}
          title="Set location"
        >
          {placementKind && (
            <>
              <div style={{ padding: '11px 13px', borderRadius: 11, background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                <div style={{ font: '600 13px Outfit, sans-serif' }}>{placementKind.productType} · {placementKind.productFormation}</div>
                <div style={{ font: '400 11.5px monospace', color: 'var(--dim)', marginTop: 2 }}>{placementKind.fabricType} · {placementKind.fabricColor}</div>
              </div>

              <Field label="Location">
                <Select
                  allowClear
                  placeholder={stockLocations.length ? 'Pick a location' : 'No locations yet — add them in Product Settings'}
                  value={placementLocation}
                  onChange={(v) => setPlacementLocation(v ?? null)}
                  style={{ width: '100%' }}
                  dropdownStyle={{ zIndex: 1200 }}
                  options={stockLocations.map((l) => ({ value: l.id, label: l.name }))}
                />
              </Field>

              <Field label={<>Bin / shelf <span style={{ color: 'var(--dim)', fontWeight: 400 }}>optional</span></>}>
                <Input placeholder="e.g. A-12" maxLength={32} value={placementBin} onChange={(e) => setPlacementBin(e.target.value)} />
              </Field>

              <div style={{ font: '400 11.5px/1.5 Outfit, sans-serif', color: 'var(--dim)' }}>
                Placement records where these units are kept. It doesn't move any stock — on hand stays {placementKind.onHand}.
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9 }}>
                <button type="button" onClick={() => setPlacementKind(null)} style={sheetCancelBtn}>Cancel</button>
                <button type="button" disabled={saving} onClick={submitPlacement} style={{ ...sheetOkBtn, opacity: saving ? 0.6 : 1 }}>Save placement</button>
              </div>
            </>
          )}
        </Sheet>
      </ConfigProvider>
    </div>
  );
}

/* ---------------------------------------------------------------------- */

const ghostBtn = {
  height: 38, padding: '0 14px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text)', font: '600 13px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
};
const primaryBtn = {
  height: 38, padding: '0 18px', borderRadius: 9, border: 'none', background: PRIMARY, color: '#fff',
  font: '700 13px Outfit, sans-serif', cursor: 'pointer',
};
const filterBtn = {
  height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
};
const rowBtn = {
  height: 30, padding: '0 11px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center',
};
const rowBtnPrimary = {
  ...rowBtn, border: `1px solid ${hexToRgba(PRIMARY, .4)}`, background: 'var(--activeBg)', color: 'var(--activeText)',
};
const mobileActionBtn = {
  flex: 1, height: 44, borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const stepBtn = {
  width: 28, height: 28, borderRadius: 7, background: 'var(--surface)', border: '1px solid var(--border)',
  color: 'var(--muted)', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none',
};
const sheetCancelBtn = {
  height: 34, padding: '0 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--muted)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer',
};
const sheetOkBtn = {
  height: 34, padding: '0 16px', borderRadius: 8, border: 'none', background: PRIMARY, color: '#fff',
  font: '700 12.5px Outfit, sans-serif', cursor: 'pointer',
};

/** A modal that adopts the shell palette, used for both the entry and placement sheets. */
function Sheet({ open, onClose, title, header, rootStyle, children }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ ...rootStyle, width: 'min(480px, 100%)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, fontFamily: 'Outfit, system-ui, sans-serif', color: 'var(--text)' }}
      >
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ font: '700 14.5px Outfit, sans-serif' }}>{title}</div>
          {header || (
            <span onClick={onClose} style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--surface2)', color: 'var(--muted)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <CloseOutlined />
            </span>
          )}
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ font: '600 12px Outfit, sans-serif', color: 'var(--muted)' }}>{label}</div>
      {children}
    </div>
  );
}

function StatTile({ icon, tint, label, value, deltaPct, deltaGoodWhenUp, note, foot, active, onClick }) {
  const clickable = !!onClick;
  const deltaTint = deltaPct === null || deltaPct === undefined
    ? 'var(--muted)'
    : (deltaPct >= 0) === !!deltaGoodWhenUp ? STATUS.ok : STATUS.errorSoft;
  return (
    <div
      role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
      style={{
        background: 'var(--surface)', border: `1px solid ${active ? tint : 'var(--border)'}`, borderRadius: 14,
        padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5,
        cursor: clickable ? 'pointer' : 'default',
        boxShadow: active ? `0 0 0 1px ${tint}` : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 19, height: 19, borderRadius: 6, background: `${tint}29`, color: tint, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
        <span style={{ font: '600 11.5px Outfit, sans-serif', color: 'var(--muted)', letterSpacing: '.02em', textTransform: 'uppercase' }}>{label}</span>
        {note && <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--muted)', marginLeft: 'auto' }}>{note}</span>}
        {!note && deltaPct !== null && deltaPct !== undefined && (
          <span style={{ font: '600 11px Outfit, sans-serif', color: deltaTint, marginLeft: 'auto' }}>{deltaPct >= 0 ? '▲' : '▼'} {Math.abs(deltaPct)}%</span>
        )}
      </div>
      <div style={{ font: '700 18px var(--mono)', color: 'var(--text)' }}>{value?.toLocaleString?.() ?? value}</div>
      {foot && <div style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)' }}>{foot}</div>}
    </div>
  );
}

function ColorSwatch({ name }) {
  const hex = COLOR_HEX[(name || '').toLowerCase()];
  return (
    <span
      title={name}
      style={{
        flex: 'none', width: 11, height: 11, borderRadius: 3,
        background: hex || 'transparent',
        border: hex ? '1px solid rgba(128,128,128,.28)' : '1px dashed var(--borderStrong)',
      }}
    />
  );
}

/** Where the kind is kept: curated location chip + the free-text bin beside it. */
function Placement({ kind }) {
  if (!kind.location && !kind.bin) {
    return <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--dim)', border: '1px dashed var(--borderStrong)', padding: '3px 8px', borderRadius: 5 }}>Not placed</span>;
  }
  return (
    <>
      {kind.location && (
        <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--text)', background: 'var(--surface2)', padding: '3px 8px', borderRadius: 5, whiteSpace: 'nowrap' }}>{kind.location}</span>
      )}
      {kind.bin && (
        <span style={{ font: '400 11.5px monospace', color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kind.bin}</span>
      )}
    </>
  );
}

function MovementRow({ movement, showKind }) {
  const meta = movementMeta(movement.type);
  const negative = movement.quantity < 0;
  const kind = movement.productKind;
  const detail = [
    movement.orderId ? `Order #${movement.orderId}` : null,
    movement.reason,
  ].filter(Boolean).join(' · ');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) 70px minmax(0,1.2fr) 90px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ padding: '11px 16px', minWidth: 0 }}>
        <span style={{ font: '600 10.5px monospace', color: meta.tint, background: meta.bg || `${meta.tint}24`, padding: '3px 7px', borderRadius: 5 }}>{meta.label}</span>
        {showKind && kind && (
          <div style={{ font: '600 12px Outfit, sans-serif', color: 'var(--text)', marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[kind.productType, kind.productFormation, kind.fabricColor].filter(Boolean).join(' · ')}
          </div>
        )}
        {detail && <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', marginTop: 5 }}>{detail}</div>}
      </div>
      <div style={{ padding: '11px 8px', textAlign: 'right', font: '700 13px monospace', color: negative ? STATUS.errorSoft : STATUS.ok }}>
        {negative ? movement.quantity : `+${movement.quantity}`}
      </div>
      <div style={{ padding: '11px 8px', font: '400 12.5px Outfit, sans-serif', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {movement.user || 'System'}
      </div>
      <div style={{ padding: '11px 16px 11px 8px', font: '400 12px monospace', color: 'var(--muted)' }}>
        {movement.createdAt ? dayjs(movement.createdAt).format('D MMM') : ''}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, tint }) {
  return (
    <div>
      <div style={{ font: '600 10.5px Outfit, sans-serif', color: 'var(--dim)', letterSpacing: '.05em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ font: '700 19px monospace', color: tint, marginTop: 2 }}>{value}</div>
    </div>
  );
}

/**
 * A filter Popover's menu. Popovers portal to <body>, outside the element the
 * screen declares its CSS variables on, so the colours are plain hex — var(--x)
 * does not resolve out there.
 */
function MenuList({ items, active, onSelect, hint, darkMode }) {
  const t = THEME[darkMode ? 'dark' : 'light'];
  return (
    <div style={{ minWidth: 180, maxHeight: 320, overflowY: 'auto', background: t.surface2, borderRadius: 8 }}>
      <div style={{ padding: 6 }}>
        {items.map((it) => {
          const isActive = active !== undefined && active === it.key;
          return (
            <div
              key={String(it.key)}
              onClick={() => onSelect(it.key)}
              style={{ height: 34, borderRadius: 8, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', background: isActive ? t.hover : 'transparent' }}
            >
              <span style={{ font: '600 12.5px Outfit, sans-serif', color: t.text }}>{it.label}</span>
              {isActive && <CheckOutlined style={{ marginLeft: 'auto', color: PRIMARY, fontSize: 11 }} />}
            </div>
          );
        })}
      </div>
      {hint && (
        <div style={{ padding: '9px 12px', borderTop: `1px solid ${t.border}`, font: '400 11.5px Outfit, sans-serif', color: t.dim, lineHeight: 1.45 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, onClear, tone }) {
  const isWarn = tone === 'warn';
  return (
    <span style={{
      height: 26, padding: '0 5px 0 9px', borderRadius: 7,
      background: isWarn ? 'rgba(251,191,36,.12)' : 'var(--surface2)',
      border: isWarn ? '1px solid rgba(251,191,36,.35)' : '1px solid var(--border)',
      color: isWarn ? STATUS.warn : 'var(--text)',
      font: '600 12px Outfit, sans-serif', display: 'flex', alignItems: 'center', gap: 7,
    }}>
      <span style={{ opacity: .75, fontWeight: 500, color: isWarn ? STATUS.warn : 'var(--muted)' }}>{label}</span>
      {value}
      <span onClick={onClear} style={{ width: 16, height: 16, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, cursor: 'pointer', background: isWarn ? 'rgba(251,191,36,.16)' : 'var(--surface)' }}>
        <CloseOutlined />
      </span>
    </span>
  );
}

function PageBtn({ children, onClick, active, disabled }) {
  return (
    <span
      onClick={disabled ? undefined : onClick}
      style={{
        height: 30, minWidth: 30, padding: '0 9px', borderRadius: 8,
        border: active ? 'none' : '1px solid var(--border)',
        background: active ? PRIMARY : 'var(--surface2)',
        color: active ? '#fff' : disabled ? 'var(--dim)' : 'var(--text)',
        font: active ? '700 12px monospace' : '600 12px monospace',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </span>
  );
}

function LoadingRow() {
  return (
    <div style={{ padding: '34px 18px', display: 'flex', justifyContent: 'center' }}>
      <Spin />
    </div>
  );
}

/**
 * Two empty states from the mock: nothing on hand (the in-stock filter is
 * hiding every kind) and an empty search.
 */
function EmptyState({ search, inStockOnly, hasFilters, onShowAll, onClear }) {
  const searching = !!search || hasFilters;
  return (
    <div style={{ padding: '32px 18px 36px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
      <span style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--surface2)', color: 'var(--dim)', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {searching ? <SearchOutlined /> : '▦'}
      </span>
      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>
        {search
          ? <>No kinds match <span style={{ fontFamily: 'monospace', fontSize: 12.5, color: 'var(--activeText)' }}>{search}</span></>
          : hasFilters ? 'No kinds match these filters' : 'No stock on hand'}
      </div>
      <div style={{ font: '400 12.5px/1.5 Outfit, sans-serif', color: 'var(--muted)', maxWidth: 320 }}>
        {searching
          ? 'Search covers product type, fabric, color, formation and location. The in-stock filter is still applied.'
          : <>Turn off <span style={{ color: 'var(--text)' }}>In stock only</span> to see every product kind and open a starting balance.</>}
      </div>
      <div style={{ display: 'flex', gap: 9, marginTop: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
        {searching && (
          <button type="button" onClick={onClear} style={{ height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer' }}>
            Clear search
          </button>
        )}
        {inStockOnly && (
          <button type="button" onClick={onShowAll} style={{ height: 32, padding: '0 14px', borderRadius: 8, border: 'none', background: PRIMARY, color: '#fff', font: '700 12.5px Outfit, sans-serif', cursor: 'pointer' }}>
            Show all kinds
          </button>
        )}
      </div>
    </div>
  );
}

export default ReadyStock;
